import type { HealthTrendPoint } from '@/domain/health';

import { toHealthPercent } from './healthDetailOverviewHelpers';

/** Minimum Y-axis top when all development scores are below 10% (avoids duplicate % ticks). */
export const HEALTH_DEVELOPMENT_LOW_RANGE_AXIS_MAX = 10;
export const HEALTH_DEVELOPMENT_LOW_RANGE_STEP = 2;

export interface HealthDevelopmentYAxisConfig {
    min: number;
    max: number;
    stepSize: number;
    decimals: number;
}

const LOW_RANGE_CONFIG: HealthDevelopmentYAxisConfig = {
    min: 0,
    max: HEALTH_DEVELOPMENT_LOW_RANGE_AXIS_MAX,
    stepSize: HEALTH_DEVELOPMENT_LOW_RANGE_STEP,
    decimals: 0,
};

const FULL_RANGE_CONFIG: HealthDevelopmentYAxisConfig = {
    min: 0,
    max: 100,
    stepSize: 20,
    decimals: 0,
};

function collectTrendPercents(trend: HealthTrendPoint[]): number[] {
    return trend
        .flatMap((point) => [
            toHealthPercent(point.score1),
            toHealthPercent(point.score2),
            toHealthPercent(point.score3),
        ])
        .filter((value): value is number => value != null);
}

/**
 * Y-axis for development trend:
 * - Below 10% data → fixed 0–10% axis (step 2) so low values do not produce duplicate tick labels
 * - 10% and above → 0–100 with step 20
 */
export function getHealthDevelopmentYAxisConfig(
    trend: HealthTrendPoint[]
): HealthDevelopmentYAxisConfig {
    const values = collectTrendPercents(trend);
    if (values.length === 0) {
        return LOW_RANGE_CONFIG;
    }

    const dataMax = Math.max(...values);
    if (dataMax < HEALTH_DEVELOPMENT_LOW_RANGE_AXIS_MAX) {
        return LOW_RANGE_CONFIG;
    }

    return FULL_RANGE_CONFIG;
}
